/**
 * SmartLink anti-fraud pre-filter — dash data-layer client.
 *
 * Thin typed wrapper around `apiClient` for the
 * `/api/v1/smartlink-fraud` surface (feature #112). Mirrors the contract
 * declared in `apps/api/src/routes/smartlink-fraud.ts`:
 *
 *   POST   /                          { smartlink_id?, rule_type, pattern,
 *                                       threshold? } → { rule }
 *   GET    /by-smartlink/:id          → { smartlink_id, rules[] }
 *   DELETE /:id                       → { ok, id } (soft-delete)
 *   POST   /check                     { smartlink_id, ip, ua, country,
 *                                       click_id } → pass | block+reason
 *
 * Keep this file dependency-free beyond the root `apiClient` — no React,
 * no TanStack, so it can be reused from server-side smoke harnesses and
 * unit tests.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Rule kinds — aligned with the API `RULE_TYPES` tuple.
// ---------------------------------------------------------------------------

export const RULE_KINDS = [
	"ip_blocklist",
	"ua_regex",
	"country_deny",
	"click_rate_cap",
] as const;
export type RuleKind = (typeof RULE_KINDS)[number];

export const RULE_KIND_LABELS: Record<RuleKind, string> = {
	ip_blocklist: "IP blocklist",
	ua_regex: "User-agent regex",
	country_deny: "Country deny-list",
	click_rate_cap: "Click-rate cap",
};

export const RULE_KIND_DESCRIPTIONS: Record<RuleKind, string> = {
	ip_blocklist:
		"Block clicks from specific IP addresses or CIDR ranges (IPv4 or IPv6).",
	ua_regex:
		"Block clicks whose User-Agent matches a JavaScript regular expression.",
	country_deny:
		"Block clicks originating from one or more ISO 3166-1 alpha-2 countries.",
	click_rate_cap:
		"Cap the number of clicks permitted per IP within a rolling 60-second window.",
};

// ---------------------------------------------------------------------------
// API row shape — mirrors `FraudRuleRow` in the backend.
// ---------------------------------------------------------------------------

export interface FraudRuleRow {
	id: string;
	tenant_id: string;
	smartlink_id: string | null;
	rule_type: RuleKind;
	pattern: string;
	threshold: number | null;
	active: boolean;
	created_at: number;
	updated_at: number;
}

export interface CheckAllowed {
	allowed: true;
}

export interface CheckBlocked {
	allowed: false;
	reason: string;
	rule_id: string;
	hit_id?: string;
}

export type CheckResult = CheckAllowed | CheckBlocked;

// ---------------------------------------------------------------------------
// Form input shape — each kind exposes a different field set, but we stash
// them on a common record so the UI can round-trip between kinds without
// losing typed state.
// ---------------------------------------------------------------------------

export interface RuleDraft {
	smartlinkId: string; // "" = tenant-wide
	kind: RuleKind;
	// ip_blocklist — one pattern per line (CIDR or bare IP).
	ipList: string;
	// ua_regex — raw JS RegExp source.
	uaRegex: string;
	// country_deny — multi-select of ISO alpha-2 codes.
	countries: string[];
	// click_rate_cap — threshold + scope label (pattern).
	threshold: string;
	rateLabel: string;
}

export const EMPTY_DRAFT: RuleDraft = {
	smartlinkId: "",
	kind: "ip_blocklist",
	ipList: "",
	uaRegex: "",
	countries: [],
	threshold: "60",
	rateLabel: "*",
};

// ---------------------------------------------------------------------------
// Validation — hand-rolled schema mirroring a Zod safeParse surface. Kept
// dependency-free so tests and smoke harnesses can re-use it verbatim.
// ---------------------------------------------------------------------------

export const MAX_PATTERN_LEN = 2048;
const SMARTLINK_ID_RE = /^[A-Za-z0-9_\-]{1,64}$/;

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export interface ValidatedRule {
	smartlink_id: string | null;
	rule_type: RuleKind;
	pattern: string;
	threshold: number | null;
}

/**
 * Validate an IPv4/IPv6 address or CIDR token. The dash uses the same loose
 * form as the backend — strict parsing happens server-side.
 */
export function isValidIpToken(token: string): boolean {
	const trimmed = token.trim();
	if (trimmed.length === 0) return false;
	const [addr, bitsStr] = trimmed.split("/");
	if (!addr) return false;
	if (bitsStr !== undefined) {
		const n = Number.parseInt(bitsStr, 10);
		if (!Number.isInteger(n) || n < 0) return false;
		const isV6 = addr.includes(":");
		if (!isV6 && n > 32) return false;
		if (isV6 && n > 128) return false;
	}
	if (addr.includes(":")) {
		return /^[0-9a-fA-F:]+$/.test(addr);
	}
	return /^(\d{1,3}\.){3}\d{1,3}$/.test(addr);
}

/**
 * Convert the draft to the backend payload (or an errors map). Mirrors the
 * server-side `validateCreate` rules but tailored for the dash form layout.
 */
export const ruleDraftSchema = {
	safeParse(input: RuleDraft): ParseResult<ValidatedRule> {
		const errors: Record<string, string> = {};

		// smartlinkId is optional; empty string → null (tenant-wide).
		let smartlinkId: string | null = null;
		const rawId = input.smartlinkId?.trim() ?? "";
		if (rawId.length > 0) {
			if (!SMARTLINK_ID_RE.test(rawId)) {
				errors.smartlinkId =
					"SmartLink ID must be up to 64 chars of letters, digits, '-', '_'.";
			} else {
				smartlinkId = rawId;
			}
		}

		if (!(RULE_KINDS as readonly string[]).includes(input.kind)) {
			errors.kind = "Pick a supported rule kind.";
		}

		let pattern = "";
		let threshold: number | null = null;

		switch (input.kind) {
			case "ip_blocklist": {
				const lines = (input.ipList ?? "")
					.split(/\r?\n|,/)
					.map((s) => s.trim())
					.filter((s) => s.length > 0);
				if (lines.length === 0) {
					errors.ipList = "Add at least one IP or CIDR.";
				} else {
					const bad = lines.find((tok) => !isValidIpToken(tok));
					if (bad) {
						errors.ipList = `Not a valid IP or CIDR: ${bad}`;
					} else {
						pattern = lines.join(",");
					}
				}
				break;
			}
			case "ua_regex": {
				const raw = input.uaRegex?.trim() ?? "";
				if (raw.length === 0) {
					errors.uaRegex = "Regex source is required.";
				} else {
					try {
						new RegExp(raw);
						pattern = raw;
					} catch {
						errors.uaRegex = "Not a valid JavaScript RegExp source.";
					}
				}
				break;
			}
			case "country_deny": {
				const codes = (input.countries ?? [])
					.map((c) => c.trim().toUpperCase())
					.filter((c) => c.length > 0);
				if (codes.length === 0) {
					errors.countries = "Pick at least one country.";
				} else if (!codes.every((c) => /^[A-Z]{2}$/.test(c))) {
					errors.countries = "Country codes must be ISO 3166-1 alpha-2.";
				} else {
					pattern = Array.from(new Set(codes)).join(",");
				}
				break;
			}
			case "click_rate_cap": {
				const thr = Number.parseInt(input.threshold ?? "", 10);
				if (!Number.isInteger(thr) || thr <= 0 || thr > 1_000_000) {
					errors.threshold = "Threshold must be a positive integer.";
				} else {
					threshold = thr;
				}
				const label = (input.rateLabel ?? "").trim();
				pattern = label.length > 0 ? label : "*";
				break;
			}
		}

		if (pattern.length > MAX_PATTERN_LEN) {
			errors.pattern = `Pattern exceeds ${MAX_PATTERN_LEN} chars.`;
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				smartlink_id: smartlinkId,
				rule_type: input.kind,
				pattern,
				threshold,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// Human-readable param preview — shown in the rules table.
// ---------------------------------------------------------------------------

export function summariseRule(rule: FraudRuleRow): string {
	switch (rule.rule_type) {
		case "ip_blocklist": {
			const items = rule.pattern.split(",").map((s) => s.trim()).filter(Boolean);
			if (items.length <= 2) return items.join(", ");
			return `${items.slice(0, 2).join(", ")} +${items.length - 2} more`;
		}
		case "ua_regex":
			return `/${rule.pattern}/`;
		case "country_deny":
			return rule.pattern.toUpperCase();
		case "click_rate_cap":
			return `${rule.threshold ?? "?"} / 60s (scope: ${rule.pattern || "*"})`;
	}
}

export function scopeLabel(rule: FraudRuleRow): string {
	return rule.smartlink_id === null ? "Tenant-wide" : `Link: ${rule.smartlink_id}`;
}

// ---------------------------------------------------------------------------
// Sample click input (for the test-check panel).
// ---------------------------------------------------------------------------

export interface CheckInput {
	smartlink_id: string;
	ip: string;
	ua: string;
	country: string;
	click_id: string;
}

export const EMPTY_CHECK: CheckInput = {
	smartlink_id: "",
	ip: "",
	ua: "",
	country: "",
	click_id: "",
};

// ---------------------------------------------------------------------------
// Remote operations
// ---------------------------------------------------------------------------

const BASE = "/smartlink-fraud";

export interface CreateRuleResponse {
	rule: FraudRuleRow;
}

export interface ListRulesResponse {
	smartlink_id: string;
	rules: FraudRuleRow[];
}

export async function createRule(payload: ValidatedRule): Promise<FraudRuleRow> {
	const res = await apiClient.post<CreateRuleResponse>(BASE, payload);
	return res.rule;
}

export async function listRules(smartlinkId: string): Promise<FraudRuleRow[]> {
	try {
		const res = await apiClient.get<ListRulesResponse>(
			`${BASE}/by-smartlink/${encodeURIComponent(smartlinkId)}`,
		);
		return res.rules ?? [];
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

export async function deleteRule(id: string): Promise<void> {
	try {
		await apiClient.delete(`${BASE}/${encodeURIComponent(id)}`);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return;
		throw err;
	}
}

export async function checkSample(input: CheckInput): Promise<CheckResult> {
	return apiClient.post<CheckResult>(`${BASE}/check`, input);
}

export const smartlinkFraudQueryKeys = {
	all: ["smartlink-fraud"] as const,
	byScope: (smartlinkId: string) =>
		[...smartlinkFraudQueryKeys.all, smartlinkId || "__tenant__"] as const,
};

// ---------------------------------------------------------------------------
// Client-side soft-delete — when the backend confirms a DELETE, the cached
// list should mark the row as inactive rather than drop it, so the table
// can still surface "archived" rules with a strike-through if the operator
// flips a toggle later. This helper is exported for the test suite.
// ---------------------------------------------------------------------------

export function applySoftDelete(
	rules: readonly FraudRuleRow[],
	id: string,
	now: number,
): FraudRuleRow[] {
	return rules.map((r) =>
		r.id === id ? { ...r, active: false, updated_at: now } : r,
	);
}

/**
 * Count only rules the operator still considers "live". Matches the server
 * ordering expectation that deleted rules linger in audit views.
 */
export function countActive(rules: readonly FraudRuleRow[]): number {
	return rules.reduce((n, r) => n + (r.active ? 1 : 0), 0);
}

// ---------------------------------------------------------------------------
// ISO 3166-1 alpha-2 subset — the country multi-select only exposes the
// ~60 codes most frequently deny-listed by iGaming and MMP tenants. The
// backend accepts any pair of letters, so operators who need more can
// type-ahead search; unknown codes fall through to the default.
// ---------------------------------------------------------------------------

export interface CountryOption {
	code: string;
	name: string;
}

export const COMMON_COUNTRIES: readonly CountryOption[] = [
	{ code: "AF", name: "Afghanistan" },
	{ code: "AR", name: "Argentina" },
	{ code: "AU", name: "Australia" },
	{ code: "BE", name: "Belgium" },
	{ code: "BR", name: "Brazil" },
	{ code: "CA", name: "Canada" },
	{ code: "CH", name: "Switzerland" },
	{ code: "CL", name: "Chile" },
	{ code: "CN", name: "China" },
	{ code: "CO", name: "Colombia" },
	{ code: "CZ", name: "Czechia" },
	{ code: "DE", name: "Germany" },
	{ code: "DK", name: "Denmark" },
	{ code: "EC", name: "Ecuador" },
	{ code: "EG", name: "Egypt" },
	{ code: "ES", name: "Spain" },
	{ code: "FI", name: "Finland" },
	{ code: "FR", name: "France" },
	{ code: "GB", name: "United Kingdom" },
	{ code: "GR", name: "Greece" },
	{ code: "HK", name: "Hong Kong" },
	{ code: "HU", name: "Hungary" },
	{ code: "ID", name: "Indonesia" },
	{ code: "IE", name: "Ireland" },
	{ code: "IL", name: "Israel" },
	{ code: "IN", name: "India" },
	{ code: "IR", name: "Iran" },
	{ code: "IT", name: "Italy" },
	{ code: "JP", name: "Japan" },
	{ code: "KR", name: "South Korea" },
	{ code: "MX", name: "Mexico" },
	{ code: "MY", name: "Malaysia" },
	{ code: "NG", name: "Nigeria" },
	{ code: "NL", name: "Netherlands" },
	{ code: "NO", name: "Norway" },
	{ code: "NZ", name: "New Zealand" },
	{ code: "PE", name: "Peru" },
	{ code: "PH", name: "Philippines" },
	{ code: "PK", name: "Pakistan" },
	{ code: "PL", name: "Poland" },
	{ code: "PT", name: "Portugal" },
	{ code: "RO", name: "Romania" },
	{ code: "RU", name: "Russia" },
	{ code: "SA", name: "Saudi Arabia" },
	{ code: "SE", name: "Sweden" },
	{ code: "SG", name: "Singapore" },
	{ code: "TH", name: "Thailand" },
	{ code: "TR", name: "Turkey" },
	{ code: "TW", name: "Taiwan" },
	{ code: "UA", name: "Ukraine" },
	{ code: "US", name: "United States" },
	{ code: "VE", name: "Venezuela" },
	{ code: "VN", name: "Vietnam" },
	{ code: "ZA", name: "South Africa" },
];
